import { Injectable } from '@angular/core'
import { RemoteData } from '@ftr/foundation'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { TenantContext } from '../lib/tenant-context'
import { ResetTenantContextAction, SetTenantContextAction } from './tenant.actions'
import { TenantStateModel } from './tenant.model'

export function defaultTenantState(): TenantStateModel {
  return {
    tenantContext: RemoteData.notAsked(),
  }
}

/**
 * Tracks which tenant workspace (court system, organization, or individual - or none)
 * the current user is operating in.
 *
 * This state does not currently drive any behaviour - it exists purely as a tracked,
 * inspectable signal while the app's tenant model is being generalised beyond courts.
 * See `TenantContext` for the important distinction between this and resource/content
 * ownership.
 *
 * Written to from the handful of places that already decide the "current" court,
 * organization, or individual:
 * - `UserState.getCurrentCourtSystem` (the `GetCurrentCourtSystemAction` handler) -
 *   covers the court-system resolvers, app bootstrap, and the court picker page, since
 *   all of them already dispatch `GetCurrentCourtSystemAction`.
 * - `OrganizationState.selectOrganization` sets an organization tenant.
 *   `OrganizationState.resetOrderState` clears tenant context.
 * - The individual route resolver (new - organizations and individuals are new tenant
 *   types with no pre-existing behaviour to preserve, unlike court systems).
 *
 * Cleared on logout via `UserState.logout` dispatching `ResetTenantContextAction` - this
 * state has no knowledge of `LogoutAction` itself (see that action's doc comment).
 */
@State<TenantStateModel>({
  name: 'tenantState',
  defaults: defaultTenantState(),
})
@Injectable()
export class TenantState {
  @Selector()
  static tenantContext(state: TenantStateModel): RemoteData<TenantContext> {
    return state.tenantContext
  }

  @Action(SetTenantContextAction)
  setTenantContext({ patchState }: StateContext<TenantStateModel>, { tenantContext }: SetTenantContextAction): void {
    patchState({ tenantContext: RemoteData.success(tenantContext) })
  }

  @Action(ResetTenantContextAction)
  reset({ setState }: StateContext<TenantStateModel>): void {
    setState(defaultTenantState())
  }
}
